import { Hexagon, MapPin, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/scroll";
import { whatsappUrl } from "@/lib/whatsapp";

const Footer = ({ onQuote }) => {
  const { t } = useLanguage();

  const navLinks = [
    { id: "servicios", label: t.nav.services },
    { id: "necesidades", label: t.nav.needs },
    { id: "modular", label: t.nav.modular },
    { id: "proceso", label: t.nav.process },
  ];

  return (
    <footer className="border-t border-cy/10 bg-panel" data-testid="footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-3">
              <span className="relative grid h-10 w-10 place-items-center">
                <Hexagon className="h-10 w-10 text-cy" strokeWidth={1.4} />
                <span className="absolute font-display text-sm font-extrabold text-ink">Q</span>
              </span>
              <span className="flex flex-col leading-none">
                <span className="font-display text-base font-bold tracking-[0.18em] text-slate-100">QUANTUM</span>
                <span className="font-mono text-[11px] tracking-[0.34em] text-cy">CENTURY</span>
              </span>
            </div>
            <p className="mt-5 font-display text-lg font-semibold text-slate-200">{t.footer.tagline}</p>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-500">{t.footer.positioning}</p>
            <div className="mt-6 flex items-center gap-2 font-mono text-xs text-em" data-testid="footer-status">
              <span className="h-2 w-2 animate-pulse rounded-full bg-em" />
              {t.footer.status}
            </div>
          </div>

          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">{t.footer.navTitle}</p>
            <ul className="mt-5 space-y-3">
              {navLinks.map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`footer-link-${l.id}`}
                    onClick={() => scrollToId(l.id)}
                    className="text-sm text-slate-400 transition-colors hover:text-cy"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">{t.footer.servicesTitle}</p>
            <ul className="mt-5 space-y-3">
              {t.services.items.map((s) => (
                <li key={s.id} className="text-sm text-slate-400">
                  {s.title}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">{t.footer.contactTitle}</p>
            <div className="mt-5 flex items-center gap-2 text-sm text-slate-400">
              <MapPin className="h-4 w-4 text-cy" />
              {t.footer.location}
            </div>
            <button
              data-testid="footer-quote-btn"
              onClick={onQuote}
              className="group mt-6 flex items-center gap-2 rounded-full border border-cy/30 px-5 py-2.5 font-display text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-cy hover:bg-cy/10"
            >
              {t.nav.quote}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              data-testid="footer-whatsapp-link"
              href={whatsappUrl(t.whatsapp.message)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-block font-mono text-xs text-em transition-colors hover:text-cy"
            >
              WhatsApp →
            </a>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-cy/10 pt-8 sm:flex-row">
          <p className="font-mono text-xs text-slate-600">{t.footer.rights}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-700">QC // 2026</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
